import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import { formatDate, parseISODate, todayStart, toISODate } from '../../../lib/datetime'

interface Props {
  visible: boolean
  /**
   * Aksiyonun mevcut termini (ISO `yyyy-MM-dd`). Formun İÇİNDE salt-okunur bir alan olarak gösterilir
   * (2026-08-14'te başlık yanından buraya taşındı) ve takvimde **devre dışı** günü belirler.
   */
  currentDeadline: string | null
  /**
   * Üst aksiyonun termini (yalnız alt aksiyonda dolu) — takvimde `maxDate`. Alt aksiyonun termini
   * üst aksiyonunkini aşamaz (066 değişmezi); sunucu da aynı kuralı uygular.
   */
  parentDeadline: string | null
  submitting: boolean
  onSubmit: (input: { requestedDate: string; requestNote: string }) => void
  onHide: () => void
}

/**
 * **Termin güncelleme talebi** pop-up'ı (087) — aksiyon detayındaki Termin Bilgi Kartı'ndan açılır.
 *
 * <p>İki alan da **zorunludur** (FR-003): yeni termin tarihi ve gerekçe. Takvim **bugünden öncesini
 * seçtirmez** (FR-009); alt aksiyonda üst terminden sonrasını da seçtirmez (FR-017).
 *
 * <p><b>Yön kısıtı yoktur</b> (FR-009a, kullanıcı kararı 2026-08-14): talep edilen tarih aksiyonun
 * mevcut termininden **önce de** olabilir. Bu yüzden `minDate` mevcut termine göre değil **bugüne**
 * göre kurulur — akış bir "uzatma" akışı değil, genel bir termin değiştirme akışıdır.
 *
 * <p><b>Tek istisna mevcut terminin kendisidir</b> (kullanıcı kararı 2026-08-14): o gün takvimde
 * **devre dışıdır**, çünkü aynı tarihi talep etmek hiçbir şeyi değiştirmez — boş bir onay kuyruğu
 * kaydı üretirdi. Kısıt bilinçli olarak **yalnız istemcide**: sunucuda eşitlik/yön kısıtı yoktur
 * (FR-009a) ve eklenmedi; buradaki amaç kullanıcıyı anlamsız bir talepten korumaktır, veri
 * bütünlüğü kuralı değil.
 */
export default function DeadlineRequestDialog({
  visible,
  currentDeadline,
  parentDeadline,
  submitting,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const [date, setDate] = useState<Date | null>(null)
  const [note, setNote] = useState('')

  // Her açılışta temiz form: önceki talebin değerleri kalırsa kullanıcı yanlışlıkla onları gönderir.
  useEffect(() => {
    if (visible) {
      setDate(null)
      setNote('')
    }
  }, [visible])

  /** Takvimde devre dışı bırakılacak gün — aksiyonun o anki termini (aşağıdaki gerekçe). */
  const currentDate = parseISODate(currentDeadline)

  const invalid = date == null || note.trim() === ''

  const submit = () => {
    if (invalid || submitting) return
    onSubmit({ requestedDate: toISODate(date), requestNote: note.trim() })
  }

  return (
    <FormDialog
      visible={visible}
      header={t('deadlineRequests.create.title')}
      submitLabel={t('deadlineRequests.create.submit')}
      submitting={submitting}
      submitDisabled={invalid}
      onSubmit={submit}
      onHide={onHide}
    >
      <FormGrid>
        {/* Mevcut tarih, başlık yanında değil FORMUN İÇİNDE salt-okunur bir alan olarak durur
            (kullanıcı isteği 2026-08-14) — onay pop-up'ıyla aynı düzen: önce mevcut, sonra yeni. */}
        <FormField
          label={t('deadlineRequests.column.currentDeadline')}
          htmlFor="deadline-request-current"
          full
        >
          <InputText
            id="deadline-request-current"
            value={currentDeadline ? formatDate(currentDeadline) : ''}
            readOnly
            disabled
          />
        </FormField>

        <FormField
          label={t('deadlineRequests.field.requestedDate')}
          htmlFor="deadline-request-date"
          required
          full
        >
          <Calendar
            inputId="deadline-request-date"
            value={date}
            onChange={(e) => setDate((e.value as Date | null) ?? null)}
            dateFormat="dd.mm.yy"
            minDate={todayStart()}
            maxDate={parseISODate(parentDeadline) ?? undefined}
            // Mevcut hedef teslim tarihi SEÇİLEMEZ (kullanıcı kararı 2026-08-14): aynı tarihi talep
            // etmek hiçbir şeyi değiştirmez, yani boş bir onay kuyruğu kaydı üretirdi. Kısıt yalnız
            // takvimdedir — sunucu tarafında yön/eşitlik kısıtı bilinçli olarak YOK (FR-009a).
            disabledDates={currentDate ? [currentDate] : undefined}
            showIcon
            readOnlyInput
            // Takvim paneli DIALOG'un DIŞINA (body'ye) çizilir. Panel dialog gövdesinin içinde
            // kalırsa gövde onu sığdırmak için kaydırma çubuğu açar ve takvimin yarısı kırpılır.
            appendTo={document.body}
          />
        </FormField>

        <FormField
          label={t('deadlineRequests.field.requestNote')}
          htmlFor="deadline-request-note"
          required
          full
        >
          <InputTextarea
            id="deadline-request-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            maxLength={4000}
            autoResize
          />
        </FormField>
      </FormGrid>
    </FormDialog>
  )
}
